/**
 * User Registration Page
 * Complete registration flow for users who received a registration email
 */

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash, faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import { Input, Button } from '../shared/components/ui'
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator'
import { registrationApi } from '../services/registrationApi'
import { ApiClientError } from '../utils/axios'
import type {
  TokenValidationResponse,
  RegistrationFormData,
  RegistrationFormErrors,
} from '../types/registration'
import logo from '../logo.jpg'

type ValidationState = 'loading' | 'valid' | 'invalid' | 'expired'

/**
 * Validate registration form data
 */
function validateForm(formData: RegistrationFormData): RegistrationFormErrors {
  const errors: RegistrationFormErrors = {}

  // First name validation
  if (!formData.firstName.trim()) {
    errors.firstName = 'First name is required'
  } else if (formData.firstName.length < 2) {
    errors.firstName = 'First name must be at least 2 characters'
  }

  // Last name validation
  if (!formData.lastName.trim()) {
    errors.lastName = 'Last name is required'
  } else if (formData.lastName.length < 2) {
    errors.lastName = 'Last name must be at least 2 characters'
  }

  // Password validation
  if (!formData.password) {
    errors.password = 'Password is required'
  } else {
    const passwordRequirements = [
      { met: formData.password.length >= 8, message: 'Password must be at least 8 characters' },
      { met: /[a-z]/.test(formData.password), message: 'Password must contain a lowercase letter' },
      { met: /\d/.test(formData.password), message: 'Password must contain a number' },
    ]

    const unmetRequirement = passwordRequirements.find((req) => !req.met)
    if (unmetRequirement) {
      errors.password = unmetRequirement.message
    }
  }

  // Confirm password validation
  if (!formData.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password'
  } else if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match'
  }

  return errors
}

/**
 * Registration Page Component
 */
export function RegisterPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  // Token validation state
  const [validationState, setValidationState] = useState<ValidationState>('loading')
  const [tokenData, setTokenData] = useState<TokenValidationResponse | null>(null)
  const [validationError, setValidationError] = useState<string>('')

  // Form state
  const [formData, setFormData] = useState<RegistrationFormData>({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  })
  const [errors, setErrors] = useState<RegistrationFormErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setValidationState('invalid')
      setValidationError('No registration token provided. Please use the link from your email.')
      return
    }

    validateTokenAsync()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  /**
   * Validate token with API
   */
  async function validateTokenAsync() {
    try {
      setValidationState('loading')
      const result = await registrationApi.validateToken(token!)
      setTokenData(result)
      setValidationState('valid')
      console.log('✅ Token validated successfully:', result.email)
    } catch (error: any) {
      console.error('❌ Token validation failed:', error)

      if (error instanceof ApiClientError) {
        const errorMessage = error.error.message || 'Invalid registration token'

        // Check if token is expired vs invalid
        if (errorMessage.toLowerCase().includes('expired')) {
          setValidationState('expired')
          setValidationError('Your registration link has expired.')
        } else {
          setValidationState('invalid')
          setValidationError(errorMessage)
        }
      } else {
        setValidationState('invalid')
        setValidationError('Unable to validate registration token. Please try again.')
      }
    }
  }

  /**
   * Handle form submission
   */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    // Client-side validation
    const validationErrors = validateForm(formData)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setSubmitting(true)
    setErrors({})

    try {
      await registrationApi.completeRegistration({
        token: token!,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      })

      console.log('✅ Registration completed successfully!')

      // Redirect to login with success message
      navigate('/login?registered=true', { replace: true })
    } catch (error: any) {
      console.error('❌ Registration failed:', error)

      if (error instanceof ApiClientError) {
        const errorMessage = error.error.message || 'Registration failed'

        // Handle specific error cases
        if (errorMessage.toLowerCase().includes('expired')) {
          setValidationState('expired')
          setValidationError('Your registration link has expired.')
        } else if (errorMessage.toLowerCase().includes('password')) {
          setErrors({ password: errorMessage })
        } else {
          setErrors({ submit: errorMessage })
        }
      } else {
        setErrors({ submit: 'Registration failed. Please try again.' })
      }
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Handle input changes
   */
  function handleInputChange(field: keyof RegistrationFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  /**
   * Render loading state
   */
  if (validationState === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
          <div className="mb-4">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Validating your registration link...</h2>
          <p className="text-gray-600 text-sm">Please wait a moment.</p>
        </div>
      </div>
    )
  }

  /**
   * Render expired token state
   */
  if (validationState === 'expired') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="mb-4">
              <img src={logo} alt="IoAg" className="h-16 mx-auto" />
            </div>
            <FontAwesomeIcon icon={faExclamationCircle} className="text-orange-500 text-5xl mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Link Expired</h2>
            <p className="text-gray-600">{validationError}</p>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              Registration links expire after 7 days for security reasons. You can request a new link below.
            </p>
          </div>

          <div className="space-y-3">
            <Link to="/forgot-password" className="block">
              <Button variant="primary" className="w-full">
                Request New Link
              </Button>
            </Link>
            <Link to="/login" className="block">
              <Button variant="outline" className="w-full">
                Back to Login
              </Button>
            </Link>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              Still having trouble?{' '}
              <Link to="/contact" className="text-blue-600 hover:text-blue-800 hover:underline">
                Contact Support
              </Link>
            </p>
          </div>
        </div>
      </div>
    )
  }

  /**
   * Render invalid token state
   */
  if (validationState === 'invalid') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="mb-4">
              <img src={logo} alt="IoAg" className="h-16 mx-auto" />
            </div>
            <FontAwesomeIcon icon={faExclamationCircle} className="text-red-500 text-5xl mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Invalid Registration Link</h2>
            <p className="text-gray-600">{validationError}</p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              This registration link is invalid or has already been used. You can request a new link below.
            </p>
          </div>

          <div className="space-y-3">
            <Link to="/forgot-password" className="block">
              <Button variant="primary" className="w-full">
                Request New Link
              </Button>
            </Link>
            <Link to="/login" className="block">
              <Button variant="outline" className="w-full">
                Back to Login
              </Button>
            </Link>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              Still having trouble?{' '}
              <Link to="/contact" className="text-blue-600 hover:text-blue-800 hover:underline">
                Contact Support
              </Link>
            </p>
          </div>
        </div>
      </div>
    )
  }

  /**
   * Render registration form (valid token)
   */
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 py-12 px-4">
      <div className="w-full max-w-md">
        {/* Registration Card */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Logo and Branding */}
          <div className="text-center mb-8">
            <div className="mb-2 flex justify-center">
              <img src={logo} alt="IoAg" className="h-16" />
            </div>
            <p className="text-gray-600 text-sm">IoT Agriculture Monitoring</p>
          </div>

          {/* Heading */}
          <h2 className="text-2xl font-semibold text-center text-gray-900 mb-2">
            Complete Your Registration
          </h2>
          <p className="text-center text-gray-600 text-sm mb-6">
            Welcome! Please set up your account to get started.
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email (Read-only) */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                value={tokenData?.email || ''}
                disabled
                className="w-full bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">This email address will be used for your account</p>
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="given-name"
                placeholder="Enter your first name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                error={!!errors.firstName}
                className="w-full"
                required
              />
              {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName}</p>}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                autoComplete="family-name"
                placeholder="Enter your last name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                error={!!errors.lastName}
                className="w-full"
                required
              />
              {errors.lastName && <p className="text-xs text-red-600 mt-1">{errors.lastName}</p>}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  error={!!errors.password}
                  className="w-full pr-10"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
            </div>

            {/* Password Strength Indicator */}
            {formData.password && (
              <div className="bg-gray-50 rounded-lg p-4">
                <PasswordStrengthIndicator password={formData.password} />
              </div>
            )}

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  error={!!errors.confirmPassword}
                  className="w-full pr-10"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {errors.submit}
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-2">
              <Button
                type="submit"
                disabled={submitting}
                loading={submitting}
                variant="primary"
                className="w-full"
              >
                {submitting ? 'Creating Account...' : 'Complete Registration'}
              </Button>
            </div>

            {/* Already have account */}
            <div className="text-center pt-2">
              <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                Already have an account? Sign in
              </Link>
            </div>
          </form>
        </div>

        {/* Help Text */}
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">
            Need help?{' '}
            <Link to="/contact" className="text-blue-600 hover:text-blue-800 hover:underline">
              Contact Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
